import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, Lock, LogOut, RefreshCw, Phone, MessageSquare, CreditCard, FileText,
  ChevronDown, ChevronRight, KeyRound, Eye, EyeOff, Activity, UserPlus, Camera, Banknote
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "../components/ui/dialog";
import { Section, Eyebrow, H1, FadeIn } from "../components/Primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JWT_KEY = "rtd-admin-jwt";

function formatDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminPage() {
  const [jwt, setJwt] = useState(() =>
    typeof window !== "undefined" ? window.localStorage.getItem(JWT_KEY) || "" : ""
  );
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (token) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
      setAuthed(true);
      if (typeof window !== "undefined") window.localStorage.setItem(JWT_KEY, token);
    } catch (err) {
      if (err?.response?.status === 401) {
        if (typeof window !== "undefined") window.localStorage.removeItem(JWT_KEY);
        setAuthed(false);
        setJwt("");
      } else {
        toast.error("Could not load admin dashboard.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (jwt) load(jwt);
  }, [jwt, load]);

  // Auto-refresh every 30s while signed in so the feed stays live
  useEffect(() => {
    if (!authed || !jwt) return;
    const interval = setInterval(() => load(jwt), 30000);
    return () => clearInterval(interval);
  }, [authed, jwt, load]);

  const onSignedIn = (token) => {
    setJwt(token);
  };

  const logout = () => {
    setJwt("");
    setData(null);
    setAuthed(false);
    if (typeof window !== "undefined") window.localStorage.removeItem(JWT_KEY);
  };

  if (!authed) {
    return <AdminLogin onSignedIn={onSignedIn} />;
  }

  return (
    <div data-testid="page-admin">
      <Section className="pt-24 pb-20">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Eyebrow>Admin</Eyebrow>
            <H1 className="mt-4">Submissions inbox.</H1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              data-testid="admin-refresh"
              onClick={() => load(jwt)}
              className="rounded-full border-zinc-300"
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <ChangePasswordDialog jwt={jwt} />
            <Button
              variant="outline"
              size="sm"
              data-testid="admin-logout"
              onClick={logout}
              className="rounded-full border-zinc-300"
            >
              <LogOut className="mr-1.5 h-3.5 w-3.5" /> Sign out
            </Button>
          </div>
        </div>

        {data && (
          <>
            <ActivityFeed events={data.events || []} />

            <div data-testid="admin-summary" className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-6">
              <SummaryStat label="Contacts" value={data.summary.contacts} />
              <SummaryStat label="Newsletter" value={data.summary.newsletter} />
              <SummaryStat label="WhatsApp" value={data.summary.whatsapp_subscriptions} />
              <SummaryStat label="Payments" value={data.summary.payments} />
              <SummaryStat label="Paid" value={data.summary.payments_paid} highlight />
              <SummaryStat label="Digitized" value={data.summary.digitized} />
            </div>

            <CollapsibleSection title="Contacts" testId="admin-section-contacts" icon={Mail} count={data.contacts.length}>
              <Table
                columns={["Name", "Email", "Phone", "Message", "Received"]}
                rows={data.contacts.map((c) => [c.name, c.email, c.phone, truncate(c.message), formatDate(c.created_at)])}
                testId="admin-table-contacts"
              />
            </CollapsibleSection>

            <CollapsibleSection title="Newsletter" testId="admin-section-newsletter" icon={Mail} count={data.newsletter.length}>
              <Table
                columns={["Email", "Subscribed at"]}
                rows={data.newsletter.map((n) => [n.email, formatDate(n.created_at)])}
                testId="admin-table-newsletter"
              />
            </CollapsibleSection>

            <CollapsibleSection title="WhatsApp Subscriptions" testId="admin-section-whatsapp" icon={Phone} count={data.whatsapp_subscriptions.length}>
              <Table
                columns={["Phone", "Register type", "Delivery time", "Subscribed at"]}
                rows={data.whatsapp_subscriptions.map((w) => [
                  w.phone, w.register_type || "—", w.delivery_time, formatDate(w.created_at),
                ])}
                testId="admin-table-whatsapp"
              />
            </CollapsibleSection>

            <CollapsibleSection title="Payments" testId="admin-section-payments" icon={CreditCard} count={data.payments.length}>
              <Table
                columns={["Plan", "Amount", "Status", "Payment", "Session ID", "Created"]}
                rows={data.payments.map((p) => [
                  p.plan_name,
                  `${p.currency?.toUpperCase()} ${p.amount}`,
                  p.status,
                  p.payment_status,
                  (p.session_id || "").slice(0, 18) + "…",
                  formatDate(p.created_at),
                ])}
                testId="admin-table-payments"
              />
            </CollapsibleSection>

            <CollapsibleSection title="Digitized registers" testId="admin-section-digitized" icon={FileText} count={data.digitized.length}>
              <Table
                columns={["Register type", "Notes", "Created"]}
                rows={data.digitized.map((d) => [d.register_type, truncate(d.notes || ""), formatDate(d.created_at)])}
                testId="admin-table-digitized"
              />
            </CollapsibleSection>
          </>
        )}
      </Section>
    </div>
  );
}

function AdminLogin({ onSignedIn }) {
  const [email, setEmail] = useState("info@registertodigital.com");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Please enter email and password.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      onSignedIn(data.access_token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-admin-login">
      <Section className="pt-24 pb-32">
        <FadeIn><Eyebrow>Admin</Eyebrow></FadeIn>
        <FadeIn delay={0.05}><H1 className="mt-6 max-w-2xl">Sign in to your dashboard.</H1></FadeIn>

        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="mt-10 max-w-md rounded-3xl border border-zinc-200 bg-white p-7"
        >
          <div>
            <Label htmlFor="adminEmail" className="text-xs font-medium uppercase tracking-wider text-zinc-500">Email</Label>
            <div className="relative mt-2">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <Input
                id="adminEmail"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="admin-email-input"
                className="h-11 rounded-lg border-zinc-200 pl-9"
              />
            </div>
          </div>

          <div className="mt-4">
            <Label htmlFor="adminPassword" className="text-xs font-medium uppercase tracking-wider text-zinc-500">Password</Label>
            <div className="relative mt-2">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <Input
                id="adminPassword"
                type={show ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                data-testid="admin-password-input"
                className="h-11 rounded-lg border-zinc-200 pl-9 pr-10"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                aria-label={show ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-md text-zinc-500 hover:bg-zinc-100"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="mt-6 w-full h-11 rounded-full bg-zinc-950 text-white hover:bg-zinc-800"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>

          <p className="mt-4 text-[11px] text-zinc-500">
            Admin email + password are set in <code>backend/.env</code>. Forgot your password?
            Update <code>ADMIN_PASSWORD</code> in <code>.env</code> and restart the server, or sign in
            and use the Change Password option.
          </p>
        </form>
      </Section>
    </div>
  );
}

function ChangePasswordDialog({ jwt }) {
  const [open, setOpen] = useState(false);
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        { old_password: oldPwd, new_password: newPwd },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );
      toast.success("Password updated.");
      setOpen(false);
      setOldPwd("");
      setNewPwd("");
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="admin-change-password"
          className="rounded-full border-zinc-300"
        >
          <KeyRound className="mr-1.5 h-3.5 w-3.5" /> Change password
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change admin password</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-change-password-form">
          <div>
            <Label htmlFor="oldPwd" className="text-xs text-zinc-600">Current password</Label>
            <Input
              id="oldPwd"
              type="password"
              value={oldPwd}
              onChange={(e) => setOldPwd(e.target.value)}
              data-testid="admin-old-password"
              className="mt-1.5 h-11 rounded-lg border-zinc-200"
            />
          </div>
          <div>
            <Label htmlFor="newPwd" className="text-xs text-zinc-600">New password (min 8 chars)</Label>
            <Input
              id="newPwd"
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              data-testid="admin-new-password"
              className="mt-1.5 h-11 rounded-lg border-zinc-200"
            />
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={busy}
              data-testid="admin-change-password-submit"
              className="rounded-full bg-zinc-950 text-white hover:bg-zinc-800"
            >
              {busy ? "Saving…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SummaryStat({ label, value, highlight }) {
  return (
    <div className={`rounded-2xl border p-5 ${highlight ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white"}`}>
      <div className={`text-[11px] font-medium uppercase tracking-wider ${highlight ? "text-zinc-300" : "text-zinc-500"}`}>{label}</div>
      <div className={`mt-1 font-poppins text-3xl font-bold tracking-tight ${highlight ? "text-white" : "text-zinc-950"}`}>{value}</div>
    </div>
  );
}

function CollapsibleSection({ title, testId, icon: Icon, count, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div data-testid={testId} className="mt-8 overflow-hidden rounded-3xl border border-zinc-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-6 py-4 hover:bg-zinc-50"
      >
        <span className="inline-flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-zinc-100 text-zinc-700">
            <Icon className="h-4 w-4" />
          </span>
          <span className="font-poppins text-base font-semibold text-zinc-950">{title}</span>
          <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-600">{count}</span>
        </span>
        {open ? <ChevronDown className="h-4 w-4 text-zinc-500" /> : <ChevronRight className="h-4 w-4 text-zinc-500" />}
      </button>
      {open && <div className="border-t border-zinc-100">{children}</div>}
    </div>
  );
}

function Table({ columns, rows, testId }) {
  if (!rows || rows.length === 0) {
    return <div className="px-6 py-10 text-center text-sm text-zinc-500">No records yet.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table data-testid={testId} className="w-full min-w-[640px] border-collapse">
        <thead>
          <tr className="bg-zinc-50 text-left text-[11px] uppercase tracking-wider text-zinc-500">
            {columns.map((c) => (
              <th key={c} className="px-6 py-3 font-medium">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-zinc-100">
              {r.map((cell, j) => (
                <td key={j} className="px-6 py-3 text-sm text-zinc-800 align-top">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function truncate(s, n = 80) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}
function formatDate(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

const EVENT_ICONS = {
  contact: UserPlus,
  newsletter: Mail,
  whatsapp: Phone,
  digitize: Camera,
  checkout_started: CreditCard,
  payment_paid: Banknote,
};

function ActivityFeed({ events }) {
  return (
    <div data-testid="admin-activity-feed" className="mt-10 rounded-3xl border border-zinc-200 bg-white p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-zinc-950 text-white">
            <Activity className="h-4 w-4" />
          </div>
          <h3 className="font-poppins text-base font-semibold text-zinc-950">Live activity</h3>
          <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] text-zinc-600">{events.length}</span>
        </div>
        <span className="text-[11px] uppercase tracking-wider text-zinc-400">Last 10 events</span>
      </div>
      {events.length === 0 ? (
        <div className="mt-6 rounded-2xl border-2 border-dashed border-zinc-200 bg-zinc-50 px-6 py-10 text-center text-sm text-zinc-500">
          No activity yet. As soon as someone fills a form, digitizes a register, or pays — it shows up here.
        </div>
      ) : (
        <ul className="mt-5 divide-y divide-zinc-100">
          {events.map((e, i) => {
            const Icon = EVENT_ICONS[e.type] || Activity;
            return (
              <li key={e.id || i} data-testid={`activity-item-${i}`} className="flex items-start gap-3 py-3">
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-zinc-200 bg-white text-zinc-700">
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-zinc-900">{e.message}</p>
                  <p className="text-[11px] text-zinc-500">
                    <span className="font-mono uppercase tracking-wider text-zinc-400">{e.type}</span>
                    <span className="mx-1.5 text-zinc-300">·</span>
                    {relativeTime(e.created_at)}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
